if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        // 启用提交按钮（防止页面加载前提交）
        if (this.submitBtn) {
          this.submitBtn.disabled = false;
        }

        if (theme.settings.afterAtc !== 'no-js') {
          this.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      let itemAdded = false;
      let errorMessage = '';

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (typeof data.errors === 'string') {
          error = data.errors;
        } else if (data.errors && typeof data.errors === 'object') {
          error = Object.values(data.errors).map((item) => (
            Array.isArray(item) ? item.join(', ') : String(item)
          )).join(', ');
        }

        if (!response.ok || data.status) {
          errorMessage = typeof error === 'string' ? error : '';
          throw new Error(response.status);
        }

        const requestedVariantId = formData.get('id');
        const addedItem = Array.isArray(data.items)
          ? data.items.find((item) => (
            item && String(item.variant_id || item.id) === String(requestedVariantId)
          ))
          : data;
        const addedVariantId = addedItem && (addedItem.variant_id || addedItem.id);
        const addedQuantity = Number(addedItem && addedItem.quantity);
        if (!requestedVariantId || String(addedVariantId) !== String(requestedVariantId)
          || !Number.isFinite(addedQuantity) || addedQuantity <= 0) {
          throw new Error('Add-to-cart response did not confirm the requested item.');
        }

        itemAdded = true;

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 200);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          // Update cart drawer contents.
          if (this.cartDrawer) {
            await this.cartDrawer.renderContents(
              data,
              !quickAddDrawer && theme.settings.afterAtc === 'drawer'
            );
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                await cartItems.refresh();
              }
            }
          }
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();

        // Calculate submitted quantity to determine if this was an add or update
        const submittedQty = parseInt(formData.get('quantity') || 1, 10);
        const itemInCart = newCartData.items.find((item) => item.variant_id === addedVariantId);
        
        // If the item quantity in the new cart is greater than what we submitted,
        // it means it was already in the cart (Update).
        // Otherwise it's likely a new item (Add).
        const isUpdate = itemInCart && (itemInCart.quantity > submittedQty);

        if (isUpdate) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: addedVariantId,
              oldQuantity: itemInCart.quantity - submittedQty,
              newQuantity: itemInCart.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: addedVariantId
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        errorMessage = errorMessage || (itemAdded
          ? (theme.strings && theme.strings.cartAddedRefreshError)
            || 'Your item was added, but your cart could not be refreshed. Please open your cart to check it.'
          : (theme.strings && (theme.strings.cartUpdateUnconfirmedError || theme.strings.cartError))
            || "We couldn't update your cart. Please check your cart before trying again.");
        this.setErrorMsgState(errorMessage);
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: errorMessage
          }
        }));
      } finally {
        const resetButton = () => {
          this.submitBtn.removeAttribute('aria-disabled');
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        };

        if (itemAdded) {
          this.submitBtn.classList.add('is-success');
          // Keep duplicate clicks blocked while the success animation completes.
          setTimeout(resetButton, 500);
        } else {
          resetButton();
        }
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
